/**
 * "What needs me today" — the console's front door.
 *
 * The design document's operating model is alert-driven: *any* reconciliation mismatch alerts
 * a superadmin, a tenant crossing 0.5 % disputes alerts a superadmin, and of `account.updated`
 * it says "you need to know before they do". The old console opened on a table and made an
 * operator visit five screens to learn that nothing was wrong.
 *
 * THE RULE THIS SCREEN EXISTS TO ENFORCE: absence of a verdict is not a good verdict. A
 * zero-count signal is OMITTED (a to-do list of zeros trains the eye to skip it), a failed
 * read is rendered as NOT-KNOWN rather than as zero or as nothing, and the books line is the
 * one signal that always shows — because its absence would itself be information.
 *
 * Rows are ordered by consequence, not by endpoint: the dispute threshold first (the platform
 * ACCOUNT is at risk), then the books, then unmatched Stripe money, then people waiting, then
 * machinery.
 */
import React from "react";

import { useT, useLanguage } from "../../lib/LanguageContext";
import { gatewayErrorText } from "../../gateway/errorText";
import {
  ALARM_ROW, ALARM_TEXT, MUTED, RULE, RULE_STRONG, SectionLabel, UNKNOWN_ROW,
} from "./Ledger";
import TreasuryStrip from "./TreasuryStrip";
import { STRIPE_DISPUTE_CLIFF_PCT } from "./thresholds";
import { useOverviewTriage } from "./useOverviewTriage";
import { freshnessStamp } from "./stamp";
import { utcDay } from "../../lib/calendarDay";
import { formatMoneyLedger } from "../../lib/formatMoney";
import { formatPercent } from "../../lib/formatNumber";



export default function OverviewScreen({ gatewayApi, onGo }) {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const tc = useT("common");
  const { signals, reload, date, oldestFetchedAt, anyLoading } = useOverviewTriage(gatewayApi);

  // The reconciliation day is a UTC calendar day inside a sentence (`utcDay` pins the zone and
  // writes the month out); `formatDate` would show the day before west of Greenwich.
  const day = utcDay(date, lang) ?? date;

  const reason = (s) => (s.code ? gatewayErrorText(s.code, tc) : t("gw.console.state.unreachable"));

  /** A line the operator can act on. `to` makes it a link into the screen that fixes it. */
  const Row = ({ id, tone, children, to }) => (
    <div className={`py-2 ${RULE} flex items-baseline justify-between gap-4 ${
      tone === "alarm" ? ALARM_ROW : tone === "unknown" ? UNKNOWN_ROW : ""}`}
         role={tone === "alarm" || tone === "unknown" ? "alert" : undefined}
         data-testid={`overview-${id}`}>
      <span className="text-sm">{children}</span>
      {to && (
        <button type="button" onClick={() => onGo(to)}
                className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-4 whitespace-nowrap"
                data-testid={`overview-${id}-go`}>
          {t("gw.console.overview.go")} →
        </button>
      )}
    </div>
  );

  // ── the books: the one line that always renders ──────────────────────────
  const books = signals.books;
  const booksVerdict = (() => {
    if (books.loading) return { tone: "plain", text: t("gw.console.state.reading") };
    if (books.error) {
      // NOT the alarm colour and NOT the all-clear. "Could not be checked" is its own state,
      // and conflating it with either is the exact mistake the reconciliation panel already
      // refuses to make.
      return { tone: "unknown", text: t("gw.console.overview.booksUnknown", { reason: reason(books) }) };
    }
    const drift = Number(books.data?.driftCents ?? 0);
    const zero = books.data?.sumZero === true || drift === 0;
    return zero
      ? { tone: "plain", text: t("gw.console.overview.booksBalance") }
      : { tone: "alarm", text: t("gw.console.overview.booksDrift", { amount: formatMoneyLedger(Math.abs(drift), books.data?.currency, lang) }) };
  })();

  const rows = [];

  // 1. Stripe's own threshold. One bad practitioner can jeopardise the whole business.
  const dr = signals.disputeRates;
  if (dr.error) {
    rows.push(<Row key="dr" id="disputeRates" tone="unknown" to="disputeRates">
      {t("gw.console.overview.disputeRatesUnknown", { reason: reason(dr) })}</Row>);
  } else if (dr.data) {
    const alertPct = Number(dr.data.alertPct ?? 0.5);
    const near = (dr.data.rows || []).filter(
      (r) => r.disputePct != null && Number(r.disputePct) >= alertPct);
    if (near.length) {
      const worst = near.reduce((a, b) => (Number(b.disputePct) > Number(a.disputePct) ? b : a));
      rows.push(<Row key="dr" id="disputeRates" tone="alarm" to="disputeRates">
        {t(Number(worst.disputePct) >= STRIPE_DISPUTE_CLIFF_PCT
          ? "gw.console.overview.disputeOverCliff"
          : "gw.console.overview.disputeNearCliff", {
          count: near.length,
          pct: formatPercent(Number(worst.disputePct), lang),
          cliff: formatPercent(STRIPE_DISPUTE_CLIFF_PCT, lang),
        })}</Row>);
    }
  }

  // 2. Money that moved which the ledger cannot explain.
  const rec = signals.reconciliation;
  if (rec.error) {
    rows.push(<Row key="rec" id="reconciliation" tone="unknown" to="reconciliation">
      {t("gw.console.overview.reconUnknown", { date: day, reason: reason(rec) })}</Row>);
  } else if (rec.data && rec.data.generated === false) {
    rows.push(<Row key="rec" id="reconciliation" tone="unknown" to="reconciliation">
      {t("gw.console.overview.reconNotRun", { date: day })}</Row>);
  } else if (rec.data) {
    const unmatched = (rec.data.stripeUnmatched || []).length;
    const mismatched = (rec.data.mismatches || []).length;
    if (unmatched || mismatched) {
      rows.push(<Row key="rec" id="reconciliation" tone="alarm" to="reconciliation">
        {t("gw.console.overview.reconProblems", { date: day, unmatched, mismatched })}</Row>);
    }
  }

  // 3. People waiting to be paid.
  const po = signals.payouts;
  if (po.error) {
    rows.push(<Row key="po" id="payouts" tone="unknown" to="payouts">
      {t("gw.console.overview.payoutsUnknown", { reason: reason(po) })}</Row>);
  } else if ((po.data?.rows || []).length) {
    rows.push(<Row key="po" id="payouts" tone="plain" to="payouts">
      {t("gw.console.overview.payoutsWaiting", { count: po.data.rows.length })}</Row>);
  }

  // 4. Machinery that gave up.
  const ev = signals.events;
  if (ev.error) {
    rows.push(<Row key="ev" id="events" tone="unknown" to="events">
      {t("gw.console.overview.eventsUnknown", { reason: reason(ev) })}</Row>);
  } else if ((ev.data?.rows || []).length) {
    rows.push(<Row key="ev" id="events" tone="alarm" to="events">
      {t("gw.console.overview.eventsGaveUp", { count: ev.data.rows.length })}</Row>);
  }

  return (
    <div data-testid="overview-screen">
      <TreasuryStrip resource={{ ...signals.liability, stale: false }} />

      <SectionLabel>{t("gw.console.overview.title")}</SectionLabel>
      <div className={`${RULE_STRONG} mt-2 mb-1`} />

      <div className={`py-2 ${RULE} text-sm ${
        booksVerdict.tone === "alarm" ? ALARM_ROW
        : booksVerdict.tone === "unknown" ? UNKNOWN_ROW : ""}`}
           role={booksVerdict.tone === "plain" ? undefined : "alert"}
           data-testid="overview-books">
        <span className={booksVerdict.tone === "alarm" ? ALARM_TEXT : ""}>{booksVerdict.text}</span>
      </div>

      {rows}

      {!anyLoading && rows.length === 0 && booksVerdict.tone === "plain" && (
        <p className={`${MUTED} py-3`} data-testid="overview-all-clear">
          {t("gw.console.overview.allClear")}
        </p>
      )}

      <div className="flex items-baseline gap-2 text-[11px] text-slate-400 tabular-nums mt-4">
        <span>
          {oldestFetchedAt
            ? t("gw.console.state.asOf", { at: freshnessStamp(oldestFetchedAt, lang) })
            : t("gw.console.state.neverRead")}
        </span>
        <span aria-hidden className="text-slate-300">·</span>
        <button type="button" onClick={reload} disabled={anyLoading}
                className="text-slate-500 hover:text-slate-900 underline underline-offset-4 disabled:opacity-50"
                data-testid="overview-refresh">
          {anyLoading ? t("gw.console.state.reading") : t("gw.console.state.refresh")}
        </button>
      </div>
    </div>
  );
}
